import React from 'react';
import { createColumnHelper, FilterFn, Row } from '@tanstack/react-table';
import { ChevronRight, Leaf } from 'lucide-react';
import { Checkbox } from '../../ui/checkbox';
import { cn } from '../../../lib/utils';
import type { CatalogueRow, StyleRow } from '../../../lib/catalogueAdminApi';
import { cleanText, count, money, moneyRange, relativeTime, sizeRangeLabel, swatchColour } from './format';

export type FilterKind = 'facet' | 'range' | 'text';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    label: string;
    align?: 'left' | 'right' | 'center';
    filter?: FilterKind;
    format?: (v: number) => string;
    step?: number;
    groupable?: boolean;
  }
}

// Style rows carry the catalogue data; variant sub-rows (one per SKU) only
// answer for the columns that make sense for a SKU and stay undefined
// elsewhere, so filters, facets and sorting ignore them.
const isStyle = (r: CatalogueRow): r is StyleRow => r.kind === 'style';
const styleOnly = <T,>(fn: (r: StyleRow) => T) => (r: CatalogueRow): T | undefined => (isStyle(r) ? fn(r) : undefined);
// A SKU sub-row displays its style's value in style-level columns, rendered
// exactly as the style row renders it; the accessor still ignores it.
const ownerStyle = (row: Row<CatalogueRow>): StyleRow | undefined => {
  if (isStyle(row.original)) return row.original;
  const parent = row.getParentRow()?.original;
  return parent && isStyle(parent) ? parent : undefined;
};
const inherited = <T,>(pick: (r: StyleRow) => T, render: (v: T) => React.ReactNode) => ({ row }: { row: Row<CatalogueRow> }) => {
  const s = ownerStyle(row);
  return s ? render(pick(s)) : null;
};

export const oneOf: FilterFn<CatalogueRow> = (row, columnId, filterValue: string[]) => {
  if (!filterValue || filterValue.length === 0) return true;
  return filterValue.includes(String(row.getValue(columnId) ?? ''));
};

export const contains: FilterFn<CatalogueRow> = (row, columnId, filterValue: string) => {
  const terms = String(filterValue ?? '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const hay = String(row.getValue(columnId) ?? '').toLowerCase();
  return terms.every((t) => hay.includes(t));
};

export const globalSearch: FilterFn<CatalogueRow> = (row, _columnId, filterValue: string) => {
  const terms = String(filterValue ?? '').toLowerCase().split(/\s+/).filter(Boolean);
  if (!terms.length) return true;
  const r = row.original;
  if (!isStyle(r)) return true;
  const hay = `${r.style_code} ${r.style_name} ${r.brand} ${r.product_type}`.toLowerCase();
  return terms.every((t) => hay.includes(t));
};

const helper = createColumnHelper<CatalogueRow>();

const H = ({ children, align }: { children: React.ReactNode; align?: 'left' | 'right' | 'center' }) => (
  <span className={cn('block truncate', align === 'right' && 'text-right', align === 'center' && 'text-center')}>{children}</span>
);
const Muted = ({ children }: { children: React.ReactNode }) => <span className="text-[color:var(--oa-muted)]">{children}</span>;
const Right = ({ children, muted }: { children: React.ReactNode; muted?: boolean }) => (
  <div className={cn('whitespace-nowrap text-right tabular-nums', muted && 'text-[color:var(--oa-muted)]')}>{children}</div>
);

const checkboxCls = 'h-4 w-4 border-[color:var(--oa-line-3)] data-[state=checked]:bg-[color:var(--oa-accent)] data-[state=checked]:border-[color:var(--oa-accent)]';

// Multi-valued tag column: quiet text, first two values, "+n", all on hover,
// facet filter per value. Grouping uses the first value.
const tagColumn = (id: string, label: string, pick: (r: StyleRow) => string[], size: number, tone: 'strong' | 'muted') =>
  helper.accessor(styleOnly(pick), {
    id,
    size,
    enableSorting: false,
    filterFn: 'arrIncludesSome',
    enableGrouping: tone === 'strong',
    getGroupingValue: (row) => (isStyle(row) ? pick(row)[0] ?? 'Unclassified' : undefined),
    meta: { label, filter: 'facet', groupable: tone === 'strong' },
    header: () => <H>{label}</H>,
    cell: inherited(pick, (tags) => {
      if (!tags.length) return null;
      const shown = tags.slice(0, 2);
      return (
        <span className={cn('block truncate', tone === 'strong' ? 'text-[color:var(--oa-text)]' : 'text-[color:var(--oa-muted)]')} title={tags.join('\n')}>
          {shown.join(' · ')}
          {tags.length > shown.length ? <span className="ml-1 text-[11px] tabular-nums text-[color:var(--oa-faint)]">+{tags.length - shown.length}</span> : null}
        </span>
      );
    }),
  });

export const buildColumns = () => [
  helper.display({
    id: 'select',
    size: 36,
    minSize: 36,
    maxSize: 36,
    enableResizing: false,
    enableHiding: false,
    enableSorting: false,
    meta: { label: 'Select' },
    header: ({ table }) => (
      <Checkbox
        aria-label="Select all rows on this page"
        checked={table.getIsAllPageRowsSelected() ? true : table.getIsSomePageRowsSelected() ? 'indeterminate' : false}
        onCheckedChange={(v) => table.toggleAllPageRowsSelected(!!v)}
        className={checkboxCls}
      />
    ),
    cell: ({ row }) =>
      isStyle(row.original) ? (
        <Checkbox
          aria-label={`Select ${row.original.style_code}`}
          checked={row.getIsSelected() ? true : row.getIsSomeSelected() ? 'indeterminate' : false}
          onCheckedChange={(v) => row.toggleSelected(!!v)}
          onClick={(e) => e.stopPropagation()}
          className={checkboxCls}
        />
      ) : null,
  }),

  helper.accessor((r) => (isStyle(r) ? cleanText(r.style_name) : `${cleanText(r.colour_name)} ${r.size_name ?? ''}`), {
    id: 'product',
    size: 300,
    minSize: 200,
    enableHiding: false,
    enableGrouping: false,
    filterFn: contains,
    meta: { label: 'Product', filter: 'text' },
    header: () => <H>Product</H>,
    sortingFn: 'text',
    cell: ({ row, getValue }) => {
      const r = row.original;
      if (!isStyle(r)) {
        return (
          <div className="flex min-w-0 items-center gap-2 pl-[26px]">
            <span className="h-3 w-3 shrink-0 rounded-full border border-black/10" style={{ background: swatchColour(r.rgb) }} />
            <span className={cn('truncate', r.sku_status === 'Live' ? 'text-[color:var(--oa-text)]' : 'text-[color:var(--oa-faint)] line-through')}>
              {cleanText(r.colour_name) || 'No colour'} <span className="text-[color:var(--oa-muted)]">·</span> {r.size_name || r.size_code || '—'}
            </span>
          </div>
        );
      }
      const open = row.getIsExpanded();
      return (
        <div className="flex min-w-0 items-center gap-2">
          <ChevronRight className={cn('h-3.5 w-3.5 shrink-0 text-[color:var(--oa-faint)] transition-transform', open && 'rotate-90 text-[color:var(--oa-text)]')} />
          <div className="h-7 w-7 shrink-0 overflow-hidden rounded border border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)]">
            {r.image_url ? <img src={r.image_url} alt="" loading="lazy" className="h-full w-full object-contain" /> : null}
          </div>
          <span className="truncate font-medium text-[color:var(--oa-text)]">{getValue()}</span>
        </div>
      );
    },
  }),

  helper.accessor((r) => (isStyle(r) ? r.style_code : r.sku_code), {
    id: 'code',
    size: 120,
    filterFn: contains,
    meta: { label: 'Code', filter: 'text' },
    header: () => <H>Code</H>,
    cell: ({ getValue }) => <span className="font-mono text-[12px] text-[color:var(--oa-muted)]">{getValue()}</span>,
  }),

  helper.accessor(styleOnly((r) => cleanText(r.brand)), {
    id: 'brand',
    size: 150,
    filterFn: oneOf,
    meta: { label: 'Brand', filter: 'facet', groupable: true },
    header: () => <H>Brand</H>,
    cell: inherited((r) => cleanText(r.brand), (v) => <span className="block truncate">{v}</span>),
  }),

  helper.accessor(styleOnly((r) => r.product_type), {
    id: 'product_type',
    size: 160,
    filterFn: oneOf,
    meta: { label: 'Type', filter: 'facet', groupable: true },
    header: () => <H>Type</H>,
    cell: inherited((r) => r.product_type, (v) => <span className="block truncate">{v}</span>),
  }),

  helper.accessor(styleOnly((r) => r.gender ?? 'Unspecified'), {
    id: 'gender',
    size: 100,
    filterFn: oneOf,
    meta: { label: 'Gender', filter: 'facet', groupable: true },
    header: () => <H>Gender</H>,
    cell: inherited((r) => r.gender ?? 'Unspecified', (v) => <Muted>{v}</Muted>),
  }),

  helper.accessor(styleOnly((r) => r.age_group ?? 'Unspecified'), {
    id: 'age_group',
    size: 90,
    filterFn: oneOf,
    meta: { label: 'Age', filter: 'facet', groupable: true },
    header: () => <H>Age</H>,
    cell: inherited((r) => r.age_group ?? 'Unspecified', (v) => <Muted>{v}</Muted>),
  }),

  tagColumn('category', 'Category', (r) => r.category, 210, 'strong'),
  tagColumn('sector', 'Sector', (r) => r.sector, 150, 'strong'),
  tagColumn('attributes', 'Attributes', (r) => r.attributes, 180, 'muted'),
  tagColumn('feed_tags', 'Feed tags', (r) => r.feed_tags, 260, 'muted'),

  helper.accessor(styleOnly((r) => r.colour_count), {
    id: 'colours',
    size: 130,
    filterFn: 'inNumberRange',
    sortUndefined: 'last',
    meta: { label: 'Colours', align: 'right', filter: 'range', format: (v) => String(Math.round(v)) },
    aggregationFn: 'sum',
    header: () => <H align="right">Colours</H>,
    cell: ({ row }) => {
      const r = row.original;
      if (!isStyle(r)) {
        return (
          <span className="flex items-center justify-end gap-2">
            <span className="truncate">{cleanText(r.colour_name) || 'No colour'}</span>
            <span className="h-3.5 w-3.5 shrink-0 rounded-full border border-white ring-1 ring-black/10" style={{ background: swatchColour(r.rgb) }} />
          </span>
        );
      }
      const shown = r.swatches.slice(0, 6);
      return (
        <span className="flex items-center justify-end gap-2">
          {shown.length ? (
            <span className="inline-flex -space-x-1">
              {shown.map((rgb, i) => (
                <span key={i} className="h-3.5 w-3.5 rounded-full border border-white ring-1 ring-black/10" style={{ background: swatchColour(rgb) }} />
              ))}
            </span>
          ) : null}
          <span className="w-5 text-right tabular-nums">{r.colour_count}</span>
        </span>
      );
    },
    aggregatedCell: ({ getValue }) => <Right muted>{count((getValue() as number) ?? 0)}</Right>,
  }),

  helper.accessor(styleOnly((r) => r.size_count), {
    id: 'sizes',
    size: 70,
    filterFn: 'inNumberRange',
    sortUndefined: 'last',
    meta: { label: 'Sizes', align: 'right', filter: 'range', format: (v) => String(Math.round(v)) },
    header: () => <H align="right">Sizes</H>,
    cell: ({ row, getValue }) => {
      const r = row.original;
      if (!isStyle(r)) return <Right>{r.size_name || r.size_code || '—'}</Right>;
      return <Right>{getValue()}</Right>;
    },
  }),

  helper.accessor(styleOnly((r) => sizeRangeLabel(r.size_range)), {
    id: 'size_range',
    size: 120,
    meta: { label: 'Size range' },
    header: () => <H>Range</H>,
    cell: inherited((r) => sizeRangeLabel(r.size_range), (v) => <span className="block truncate text-[color:var(--oa-muted)]">{v || ''}</span>),
  }),

  helper.accessor(styleOnly((r) => r.variant_count), {
    id: 'variants',
    size: 80,
    filterFn: 'inNumberRange',
    sortUndefined: 'last',
    meta: { label: 'SKUs', align: 'right', filter: 'range', format: (v) => String(Math.round(v)) },
    aggregationFn: 'sum',
    header: () => <H align="right">SKUs</H>,
    cell: ({ row, getValue }) => {
      const r = row.original;
      if (!isStyle(r)) return <Right>{r.sku_code}</Right>;
      return <Right>{count(getValue() ?? 0)}</Right>;
    },
    aggregatedCell: ({ getValue }) => <Right>{count((getValue() as number) ?? 0)}</Right>,
  }),

  helper.accessor((r) => (isStyle(r) ? r.cost_min ?? undefined : r.single_price ?? undefined), {
    id: 'cost',
    size: 130,
    sortUndefined: 'last',
    filterFn: 'inNumberRange',
    meta: { label: 'Cost', align: 'right', filter: 'range', format: money, step: 0.5 },
    aggregationFn: 'min',
    header: () => <H align="right">Cost</H>,
    cell: ({ row }) => {
      const r = row.original;
      if (!isStyle(r)) return <Right>{money(r.single_price)}</Right>;
      return <Right>{moneyRange(r.cost_min, r.cost_max)}</Right>;
    },
    aggregatedCell: ({ getValue }) => <Right muted>from {money(getValue() as number)}</Right>,
  }),

  helper.accessor(styleOnly((r) => (r.sustainable_organic === 'Yes' ? 'Sustainable' : 'Standard')), {
    id: 'sustainable',
    size: 60,
    filterFn: oneOf,
    meta: { label: 'Eco', align: 'center', filter: 'facet', groupable: true },
    header: () => <H align="center">Eco</H>,
    cell: inherited((r) => r.sustainable_organic === 'Yes', (eco) =>
      eco ? (
        <div className="flex justify-center" title="Sustainable / organic"><Leaf className="h-4 w-4 text-[color:var(--oa-accent-2)]" /></div>
      ) : null),
  }),

  helper.accessor(styleOnly((r) => cleanText(r.fabric)), {
    id: 'fabric',
    size: 220,
    filterFn: contains,
    meta: { label: 'Fabric', filter: 'text' },
    header: () => <H>Fabric</H>,
    cell: inherited((r) => cleanText(r.fabric), (v) => <span className="block truncate text-[color:var(--oa-muted)]" title={v}>{v ?? ''}</span>),
  }),

  helper.accessor(styleOnly((r) => r.country_of_origin ?? 'Unknown'), {
    id: 'origin',
    size: 90,
    filterFn: oneOf,
    meta: { label: 'Origin', filter: 'facet', groupable: true },
    header: () => <H>Origin</H>,
    cell: inherited((r) => r.country_of_origin ?? 'Unknown', (v) => <Muted>{v}</Muted>),
  }),

  helper.accessor(styleOnly((r) => r.updated_at), {
    id: 'updated_at',
    size: 100,
    meta: { label: 'Updated' },
    header: () => <H>Updated</H>,
    cell: ({ row }) => {
      const at = row.original.updated_at;
      return <Muted>{at ? relativeTime(at) : ''}</Muted>;
    },
  }),
];

export const DEFAULT_HIDDEN: Record<string, boolean> = {
  code: false,
  gender: false,
  age_group: false,
  attributes: false,
  feed_tags: false,
  size_range: false,
  cost: false,
  fabric: false,
  origin: false,
  updated_at: false,
};

export const rowsToCsv = (rows: Row<CatalogueRow>[]): string => {
  const cols = ['style_code', 'style_name', 'brand', 'product_type', 'category', 'sector', 'attributes', 'feed_tags', 'gender', 'age_group', 'skus', 'live_skus', 'colours', 'sizes', 'size_range', 'cost_min', 'cost_max', 'sustainable', 'fabric', 'origin'];
  const esc = (v: unknown) => {
    const s = v === null || v === undefined ? '' : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = rows
    .map((row) => row.original)
    .filter(isStyle)
    .map((r) =>
      [
        r.style_code, cleanText(r.style_name), cleanText(r.brand), r.product_type, r.category.join(' | '), r.sector.join(' | '), r.attributes.join(' | '), r.feed_tags.join(' | '), r.gender ?? '', r.age_group ?? '',
        r.variant_count, r.live_count, r.colour_count, r.size_count, sizeRangeLabel(r.size_range),
        r.cost_min ?? '', r.cost_max ?? '', r.sustainable_organic ?? '', cleanText(r.fabric), r.country_of_origin ?? '',
      ].map(esc).join(',')
    );
  return [cols.join(','), ...lines].join('\n');
};
